// How a Social Security Number reads once it's recorded (OLIO-2586).
//
// The full number is shown in exactly one place — the entry form, where someone has to be able to
// read what they're confirming or correcting. Every read-only surface uses this instead: the
// extraction card and the referral header today, and anywhere else it turns up later.
//
// The format lives here alone so changing it is one edit rather than a hunt. isValidSsn lives here
// for the same reason — it is the inverse of the masking problem below, and the two have to agree
// about what "digits" means.

const LAST_FOUR = 4;

// A complete Social Security Number, in digits.
export const SSN_LENGTH = 9;

// What gets STORED, and what the form's state holds: digits only, capped at nine.
//
// Digits rather than the dashed form on purpose. Extraction is told to write "digits only"
// (AttributeExtraction::SSN_SCHEMA), so a form that stored 123-45-6789 would put two shapes of the
// same number in the store — and on edit the dashed field would diff against the stored digits,
// rewrite the SSN on every save, and re-run every step that reads it. Reading a stored value through
// this keeps both sides of that comparison in one shape.
export function ssnDigits(value: string | null | undefined): string {
  return (value ?? '').replace(/\D/g, '').slice(0, SSN_LENGTH);
}

// What gets DISPLAYED in the entry field: the dashed grouping, filled in as it's typed.
// The field exists so someone can check a number against the packet, and 123-45-6789 is the shape
// it's printed in — nine undelimited digits are meaningfully harder to compare.
export function formatSsn(value: string | null | undefined): string {
  const digits = ssnDigits(value);
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;

  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
}

// Started but not finished. Empty stays valid — the number is optional, and a packet that doesn't
// state one is the common case. Only a half-entered number is worth stopping, and only when a person
// typed it: every masked surface renders a partial as ***-**-1234, exactly like a whole one, so it
// reads as a number on file while being unusable as one.
//
// Nothing consumes patient_ssn beyond display today, so this is about not recording a half-truth
// rather than about a broken lookup. A referral can also arrive holding a partial from a packet that
// printed the number masked — that one is left alone; see ssnEdited in ReferralModal.
export function isPartialSsn(value: string | null | undefined): boolean {
  const { length } = ssnDigits(value);

  return length > 0 && length < SSN_LENGTH;
}

// "***-**-1234" per the mock. Digits are pulled out first because a packet may state it as
// 123-45-6789 or 123456789 and both should mask identically. Anything too short to have a last
// four is masked whole rather than partially revealed — a 3-digit fragment showing in full is
// worse than a row that reads as redacted.
export function maskSsn(value: string | null | undefined): string {
  const digits = (value ?? '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length < LAST_FOUR) return '***-**-****';

  return `***-**-${digits.slice(-LAST_FOUR)}`;
}

// Placeholder numbers that pass every structural rule but are never real. 078-05-1120 is the
// Woolworth wallet-card number, still submitted decades later. Mirrors the integration platform's
// _INVALID_SSNS — the same list, deliberately, so the client never offers a Send the server refuses.
const PLACEHOLDER_SSNS = new Set(['123456789', '078051120', '219099999', '457555462']);

// Why this exists at all, and why it is not merely nice-to-have validation: maskSsn strips non-digits
// BEFORE masking, so an extracted "XXX-XX-1234" reduces to "1234" and renders as "***-**-1234" —
// byte-identical to a real number's mask. Every read-only surface in the app therefore displays a
// redacted value as though it were valid, and nobody can see the difference. Sending it to MatrixCare
// would seed a PERMANENT resident-matching key with something unverifiable.
//
// Stripping only separators (not all non-digits) is what catches those forms: `X` and `*` survive the
// strip, so a masked value fails the digits check instead of sneaking through as a 4-digit number.
//
// Rules are the SSA's own and match app/util/patient_sync/field_schema.py's ssn_errors() rule-for-rule.
// Returns null when valid — or when EMPTY, because an SSN is optional. "Fix or clear it" has to stay a
// real way out: clearing may then trip MatrixCare's any-of rule, but that is an honest error chain
// rather than a dead end.
export function ssnError(value: string | null | undefined): string | null {
  const text = (value ?? '').trim();
  if (!text) return null;

  const digits = text.replace(/[\s-]/g, '');
  if (!/^\d+$/.test(digits) || digits.length !== SSN_LENGTH) {
    return "This doesn't look like a complete SSN — it may have been redacted in the source document. Correct it or clear it.";
  }

  const [area, group, serial] = [digits.slice(0, 3), digits.slice(3, 5), digits.slice(5)];
  if (area === '000' || area === '666' || area.startsWith('9')) return `${area} is not a valid SSN area number.`;
  if (group === '00') return 'An SSN cannot have 00 as its middle group.';
  if (serial === '0000') return 'An SSN cannot end in 0000.';
  if (PLACEHOLDER_SSNS.has(digits) || new Set(digits).size === 1) {
    return 'This looks like a placeholder rather than a real number.';
  }

  return null;
}

export function isValidSsn(value: string | null | undefined): boolean {
  return ssnError(value) === null;
}
